import {
  computeDocument,
  uuidv7,
  type PaymentMethod,
  type Transaction,
  type TransactionLineInput,
} from '@iobiz/shared'
import { dbFor, type LocalRow } from '../db.js'
import { enqueue, runCycle } from '../sync/engine.js'
import { lireVente } from './sales.local.js'
import { deviceId } from '../device.js'

/**
 * Encaissement en mode local-first.
 *
 * ⭐ La vente est écrite dans la réplique AVANT tout réseau, puis poussée. Le
 * commerçant rend la monnaie et passe au client suivant sans jamais attendre le
 * serveur — c'est la promesse du produit, et la caisse est l'écran où elle se
 * tient ou s'effondre.
 */

const CLEF_COMPTEUR = 'compteurLocal'

/**
 * Référence provisoire, de la forme `L-2026-A3F7-0042`.
 *
 * ⚠️ L'appareil ne peut PAS attribuer de numéro légal : la numérotation exige
 * un verrou sur la séquence, donc le serveur. Mais un ticket sans référence est
 * introuvable, et le client en réclame une. Le « L » dit franchement qu'elle
 * est locale et provisoire.
 *
 * ⚠️ Le segment du milieu identifie l'APPAREIL, et il n'est pas décoratif : le
 * compteur est local, donc deux caisses travaillant hors ligne le même jour
 * émettraient toutes deux « L-2026-0001 ». Deux ventes distinctes porteraient
 * la même référence, et plus rien ne permettrait de les départager — ni au
 * comptoir, ni dans la base après synchronisation.
 *
 * ⚠️ Le préfixe distingue la VENTE du DEVIS. Les deux partagent ce compteur,
 * et un devis affichait donc « L-2026-… » comme une vente encaissée hors
 * ligne : on cherchait pourquoi elle n'avait jamais reçu son numéro définitif,
 * alors qu'un devis n'en reçoit jamais — c'est un brouillon par nature.
 */
async function referenceProvisoire(storeId: string, prefixe = 'L'): Promise<string> {
  const db = dbFor(storeId)
  const meta = await db.syncMeta.get(CLEF_COMPTEUR)
  const suivant = Number((meta?.value as number) ?? 0) + 1
  await db.syncMeta.put({ key: CLEF_COMPTEUR, value: suivant })
  const appareil = deviceId().replace(/-/g, '').slice(-4).toUpperCase()
  return `${prefixe}-${new Date().getFullYear()}-${appareil}-${String(suivant).padStart(4, '0')}`
}

export interface PaiementSaisi {
  method: PaymentMethod
  /** Ce qui est IMPUTÉ à la vente — jamais plus que ce qui restait dû. */
  amount: number
  /** Ce que le client a remis, quand il a fallu rendre la monnaie. */
  tendered?: number
  /** La monnaie rendue. Absents tous les deux quand il n'y a rien eu à rendre. */
  change?: number
  reference?: string
}

/**
 * Enregistre une vente comptoir.
 *
 * Les totaux sont calculés ici par `computeDocument` — le MÊME code que le
 * serveur rejouera à l'arrivée. Le ticket remis au client affiche donc, au
 * centime près, ce que la base finira par contenir.
 */
export async function encaisserVente(
  storeId: string,
  entree: {
    lines: TransactionLineInput[]
    customerId?: string | undefined
    globalDiscountRate?: number
    currencyCode: string
    sessionId?: string | undefined
    /**
     * ⚠️ Noms repris à la SAISIE, pas attendus du serveur. Une vente encaissée
     * hors ligne s'affiche aussitôt dans le journal : sans eux, les colonnes
     * « Client » et « Encaissé par » resteraient vides jusqu'à la
     * synchronisation, et le caissier croirait l'information perdue.
     */
    customerName?: string | undefined
    cashierName?: string | undefined
    /**
     * Document dont cette vente DÉCOULE — le devis accepté.
     *
     * ⭐ Sans ce lien, le devis et la vente qui en sort restent deux documents
     * étrangers : le journal ne dit pas d'où vient la facture, et le devis ne
     * dit pas s'il a abouti. Le rattachement se fait à l'encaissement, seul
     * moment où l'on sait que le client a dit oui.
     */
    parentId?: string | undefined
  },
  paiements: PaiementSaisi[],
): Promise<Transaction> {
  const db = dbFor(storeId)
  const id = uuidv7()
  const localRef = await referenceProvisoire(storeId)
  const maintenant = new Date().toISOString()
  const jour = maintenant.slice(0, 10)

  const totaux = computeDocument({
    lines: entree.lines.map((l) => ({
      quantity: l.quantity,
      unitPrice: l.unitPrice,
      discountRate: l.discountRate,
      taxRate: l.taxRate,
    })),
    currency: entree.currencyCode as never,
    globalDiscountRate: entree.globalDiscountRate ?? 0,
  })

  const regle = paiements.reduce((somme, p) => somme + p.amount, 0)

  const ligne: LocalRow = {
    id,
    doc_type: 'invoice',
    number: null,
    local_ref: localRef,
    customer_id: entree.customerId ?? null,
    customer_name: entree.customerName ?? null,
    cashier_name: entree.cashierName ?? null,
    status: regle >= totaux.totalAmount.amount ? 'paid' : 'partially_paid',
    issue_date: jour,
    due_date: null,
    currency_code: entree.currencyCode,
    subtotal_amount: totaux.subtotalAmount.amount,
    discount_amount: totaux.discountAmount.amount,
    tax_amount: totaux.taxAmount.amount,
    total_amount: totaux.totalAmount.amount,
    paid_amount: regle,
    tax_breakdown: totaux.taxBreakdown.map((t) => ({
      rate: t.rate,
      base: t.base.amount,
      amount: t.amount.amount,
    })),
    notes: null,
    terms: null,
    parent_id: entree.parentId ?? null,
    created_offline: true,
    validated_at: maintenant,
    created_at: maintenant,
    updated_at: maintenant,
    version: 0,
    lines: entree.lines.map((l, index) => ({
      id: l.id ?? uuidv7(),
      position: index,
      item_id: l.itemId ?? null,
      label: l.label,
      description: l.description ?? null,
      quantity: l.quantity,
      unit_price: l.unitPrice,
      discount_rate: l.discountRate ?? 0,
      discount_amount: totaux.lines[index]!.discountAmount.amount,
      tax_rate: l.taxRate ?? 0,
      subtotal: totaux.lines[index]!.subtotal.amount,
      tax_amount: totaux.lines[index]!.taxAmount.amount,
      total: totaux.lines[index]!.total.amount,
    })),
    payments: paiements.map((p) => ({
      id: uuidv7(),
      method: p.method,
      amount: p.amount,
      // ⚠️ Écrits DANS la réplique locale, pas seulement poussés : c'est elle
      // que lit le ticket réimprimé depuis la fiche, y compris hors ligne.
      tendered_amount: p.tendered ?? null,
      change_amount: p.change ?? null,
      currency_code: entree.currencyCode,
      received_at: maintenant,
      reference: p.reference ?? null,
      notes: null,
      created_by_name: null,
    })),
    _dirty: 1,
    _localOnly: 1,
  } as unknown as LocalRow

  await db.transactions.put(ligne)

  /**
   * ⭐ La vente date l'activité du client DANS LA RÉPLIQUE aussi.
   *
   * Le serveur fait de même à la validation, mais son résultat ne redescend
   * qu'au cycle suivant. Sans cette écriture locale, le commerçant qui vient
   * d'encaisser verrait « Dernière activité » inchangée — et conclurait que
   * la fonction ne marche pas.
   */
  if (entree.customerId) {
    const client = await db.customers.get(entree.customerId)
    if (client) {
      await db.customers.put({
        ...client,
        last_activity_at: maintenant,
        updated_at: maintenant,
      } as typeof client)
    }
  }

  await enqueue(storeId, {
    entity: 'transaction',
    entityId: id,
    op: 'create',
    // ⚠️ TOUT part ensemble — en-tête, lignes et règlements. Trois mutations
    // séparées pourraient arriver dans le désordre et laisser une vente
    // validée mais non encaissée côté serveur.
    payload: {
      id,
      docType: 'invoice',
      customerId: entree.customerId,
      issueDate: jour,
      currencyCode: entree.currencyCode,
      globalDiscountRate: entree.globalDiscountRate ?? 0,
      localRef,
      parentId: entree.parentId,
      sessionId: entree.sessionId,
      validated: true,
      lines: entree.lines,
      paiements: paiements.map((p) => ({
        method: p.method,
        amount: p.amount,
        ...(p.tendered === undefined ? {} : { tendered: p.tendered }),
        ...(p.change === undefined ? {} : { change: p.change }),
        ...(p.reference ? { reference: p.reference } : {}),
        ...(entree.sessionId ? { sessionId: entree.sessionId } : {}),
      })),
    },
    baseVersion: null,
  })
  void runCycle(storeId)

  return (await lireVente(storeId, id))!
}

/**
 * Enregistre un DEVIS ou une COMMANDE.
 *
 * ⭐ Même chemin local-first que l'encaissement : écrit dans la réplique, puis
 * mis en file. Un devis se prépare souvent debout à côté du client, et parfois
 * hors ligne — attendre le serveur pour lui annoncer un prix n'aurait pas de
 * sens.
 *
 * ⭐ `validated: true` : le SERVEUR lui attribue son numéro, `DE-2026-0001`,
 * dans la même suite continue que les factures. Une référence d'appareil
 * (`D-2026-F300-0008`) ne s'annonce pas au téléphone et ne ressemble à rien
 * sur un document remis à un client.
 *
 * ⚠️ Valider ne veut PAS dire encaisser. Ni le devis ni la commande ne sortent
 * de stock (`affectsStock` est faux pour les deux) et aucun n'entre dans le
 * chiffre d'affaires (`signeDansLeChiffre` vaut zéro) : ce sont des
 * engagements, pas des recettes — le chiffre se reconnaît à la facture. Seul
 * leur numéro devient définitif.
 */
export async function creerDocument(
  storeId: string,
  /**
   * ⚠️ Une FACTURE créée ici naît en brouillon, sans numéro : elle n'en
   * recevra un qu'à la validation. C'est ce qui permet de poser un rendez-vous
   * dès aujourd'hui pour une prestation du mois prochain sans percer un trou
   * dans la numérotation légale le jour où le client se décommande.
   */
  docType: 'quote' | 'order' | 'invoice',
  entree: {
    lines: TransactionLineInput[]
    customerId?: string | undefined
    customerName?: string | undefined
    currencyCode: string
    globalDiscountRate?: number
    notes?: string | undefined
  },
): Promise<Transaction> {
  const db = dbFor(storeId)
  const id = uuidv7()
  const maintenant = new Date().toISOString()
  const jour = maintenant.slice(0, 10)

  const totaux = computeDocument({
    lines: entree.lines.map((l) => ({
      quantity: l.quantity,
      unitPrice: l.unitPrice,
      discountRate: l.discountRate,
      taxRate: l.taxRate,
    })),
    currency: entree.currencyCode as never,
    globalDiscountRate: entree.globalDiscountRate ?? 0,
  })

  const ligne: LocalRow = {
    id,
    doc_type: docType,
    number: null,
    // ⚠️ Une référence locale MALGRÉ TOUT : sans elle, la liste afficherait
    // « — » et le devis serait innommable au téléphone.
    // « D » pour un devis, « C » pour une commande : la référence locale doit
    // se distinguer d'un ticket de caisse hors ligne, qui porte « L ».
    local_ref: await referenceProvisoire(
      storeId,
      docType === 'quote' ? 'D' : docType === 'order' ? 'C' : 'F',
    ),
    customer_id: entree.customerId ?? null,
    customer_name: entree.customerName ?? null,
    cashier_name: null,
    status: 'draft',
    issue_date: jour,
    due_date: null,
    currency_code: entree.currencyCode,
    subtotal_amount: totaux.subtotalAmount.amount,
    discount_amount: totaux.discountAmount.amount,
    tax_amount: totaux.taxAmount.amount,
    total_amount: totaux.totalAmount.amount,
    paid_amount: 0,
    tax_breakdown: totaux.taxBreakdown.map((t) => ({
      rate: t.rate,
      base: t.base.amount,
      amount: t.amount.amount,
    })),
    notes: entree.notes ?? null,
    terms: null,
    parent_id: null,
    created_offline: true,
    validated_at: null,
    created_at: maintenant,
    updated_at: maintenant,
    version: 0,
    lines: entree.lines.map((l, index) => ({
      id: l.id ?? uuidv7(),
      position: index,
      item_id: l.itemId ?? null,
      label: l.label,
      description: l.description ?? null,
      quantity: l.quantity,
      unit_price: l.unitPrice,
      discount_rate: l.discountRate ?? 0,
      discount_amount: totaux.lines[index]!.discountAmount.amount,
      tax_rate: l.taxRate ?? 0,
      subtotal: totaux.lines[index]!.subtotal.amount,
      tax_amount: totaux.lines[index]!.taxAmount.amount,
      total: totaux.lines[index]!.total.amount,
    })),
    payments: [],
    _dirty: 1,
    _localOnly: 1,
  } as unknown as LocalRow

  await db.transactions.put(ligne)

  await enqueue(storeId, {
    entity: 'transaction',
    entityId: id,
    op: 'create',
    payload: {
      id,
      docType,
      customerId: entree.customerId,
      issueDate: jour,
      currencyCode: entree.currencyCode,
      globalDiscountRate: entree.globalDiscountRate ?? 0,
      localRef: ligne['local_ref'],
      validated: true,
      lines: entree.lines,
      paiements: [],
    },
    baseVersion: null,
  })
  void runCycle(storeId)

  return (await lireVente(storeId, id))!
}
